import axiosInstance from "utils/axios";

/**
 * Extract user ID and company ID from JWT token stored in localStorage
 */
const getTokenData = () => {
  try {
    const token = localStorage.getItem('authToken');
    if (!token) return { userId: null, companyId: null };

    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      userId: payload.nameid || null,
      companyId: payload.CompanyId || null,
    };
  } catch (error) {
    console.error('Error decoding token:', error);
    return { userId: null, companyId: null };
  }
};

/**
 * Fetch lead details by lead ID
 * @param {string} leadId - The ID of the lead to fetch
 * @returns {Promise} Response with lead details data
 */
export const getLeadDetails = async (leadId) => {
  try {
    if (!leadId) {
      return {
        success: false,
        message: 'Lead ID is required',
        data: null,
      };
    }

    const response = await axiosInstance.get(`/api/Leads/${leadId}`);
    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      let leadData = resData.data;

      // Parse the data if it's a JSON string
      if (typeof resData.data === 'string') {
        try {
          leadData = JSON.parse(resData.data);
        } catch (parseError) {
          console.error('Error parsing lead data:', parseError);
          leadData = resData.data;
        }
      }

      return {
        success: true,
        message: resData.message || 'Lead details fetched successfully',
        data: leadData,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || 'Failed to fetch lead details',
      data: null,
    };
  } catch (error) {
    console.error('Error fetching lead details:', error);

    return {
      success: false,
      message: 'Something went wrong while fetching lead details',
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

/**
 * Fetch applicant leads for the current user with pagination and search
 * @param {Object} options - Query options
 * @param {string} options.search - Search term to filter leads
 * @param {number} options.pageNumber - Page number (1-indexed)
 * @param {number} options.pageSize - Number of records per page
 * @returns {Promise} Response with applicant leads data
 */
export const getApplicantLeads = async (options = {}) => {
  try {
    const { userId, companyId } = getTokenData();

    if (!userId || !companyId) {
      return {
        success: false,
        message: 'User ID or Company ID not found in token',
        data: [],
        totalRecords: 0,
      };
    }

    const {
      search = '',
      pageNumber = 1,
      pageSize = 10,
      isApplication = false,
    } = options;

    const payload = {
      companyId,
      userId,
      loggedInUserId: userId,
      search: search || '',
      pageNumber: pageNumber, // Convert to 0-indexed for API
      pageSize,
      isApplication,
    };

    const response = await axiosInstance.post('/api/Applicant/GetApplicantLeads', payload);

    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      let parsedData = [];

      try {
        // Parse the stringified JSON data (since API returns `data` as a string)
        parsedData = JSON.parse(resData.data || '[]');
      } catch (err) {
        console.error('Error parsing applicant leads data:', err);
      }

      return {
        success: true,
        message: resData.message || 'Applicant leads fetched successfully',
        data: parsedData,
        totalRecords: resData.totalRecords || parsedData.length || 0,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || 'Failed to fetch applicant leads',
      data: [],
      totalRecords: 0,
    };
  } catch (error) {
    console.error('Error fetching applicant leads:', error);

    return {
      success: false,
      message: 'Something went wrong while fetching applicant leads',
      error: error.response?.data?.message || error.message,
      data: [],
      totalRecords: 0,
    };
  }
};

/**
 * Fetch application form data by application ID
 * @param {string} applicationId - The ID of the application to fetch
 * @returns {Promise} Response with application form data including steps and fields
 */
export const getApplicationById = async (applicationId) => {
  try {
    if (!applicationId) {
      return {
        success: false,
        message: 'Application ID is required',
        data: null,
      };
    }

    const response = await axiosInstance.get(`/api/Applicant/GetApplicationById/${applicationId}`);
    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      let applicationData = resData.data;

      // Parse the data if it's a JSON string
      if (typeof resData.data === 'string') {
        try {
          applicationData = JSON.parse(resData.data);
        } catch (parseError) {
          console.error('Error parsing application data:', parseError);
          applicationData = resData.data;
        }
      }

      return {
        success: true,
        message: resData.message || 'Application form retrieved successfully',
        data: applicationData,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || 'Failed to fetch application',
      data: null,
    };
  } catch (error) {
    console.error('Error fetching application:', error);

    return {
      success: false,
      message: 'Something went wrong while fetching application',
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

/**
 * Submit application by application ID
 * @param {string} applicationId - The ID of the application to submit
 * @returns {Promise} Response with submission status and data
 */
export const submitApplicationById = async (applicationId) => {
  try {
    if (!applicationId) {
      return {
        success: false,
        message: 'Application ID is required',
        data: null,
      };
    }

    const response = await axiosInstance.get(`/api/Applicant/SubmitApplicationById/${applicationId}`);
    const resData = response.data;

    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      let submissionData = resData.data;

      if (typeof submissionData === 'string') {
        try {
          submissionData = JSON.parse(submissionData);
        } catch (parseError) {
          console.error('Error parsing application submission data:', parseError);
        }
      }

      return {
        success: true,
        message: resData.message || 'Application submitted successfully',
        data: submissionData,
      };
    }

    return {
      success: false,
      message: resData?.message || 'Failed to submit application',
      data: null,
    };
  } catch (error) {
    console.error('Error submitting application:', error);

    return {
      success: false,
      message: 'Something went wrong while submitting application',
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

/**
 * Save application field progress
 * @param {object} payload - Field progress data
 * @returns {Promise} Response with save status
 */
export const saveApplicationFieldProgress = async (payload) => {
  try {
    const { companyId } = getTokenData();
    payload.companyId = companyId;
    if (!payload?.applicationId || payload?.fieldId === undefined || payload?.value === undefined) {
      return {
        success: false,
        message: 'Application ID, Field ID, and Value are required',
        data: null,
      };
    }

    const response = await axiosInstance.post('/api/Applicant/SaveApplicationFieldProgress', payload, { showLoader: false });
    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      return {
        success: true,
        message: resData.message || 'Field saved successfully',
        data: resData.data,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || 'Failed to save field progress',
      data: null,
    };
  } catch (error) {
    console.error('Error saving application field progress:', error);

    return {
      success: false,
      message: 'Something went wrong while saving field progress',
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

/**
 * Fetch hostel rates for a student
 * @param {boolean} isInternational - Whether the student is international
 * @returns {Promise} Response with hostel rates data
 */
export const getHostelRatesForStudent = async (isInternational = false) => {
  try {
    const response = await axiosInstance.get(`/api/Applicant/GetHostelRatesForStudent`, {
      params: { IsInternational: isInternational }
    });
    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      let ratesData = [];

      try {
        // Parse the stringified JSON data
        ratesData = JSON.parse(resData.data || '[]');
      } catch (err) {
        console.error('Error parsing hostel rates data:', err);
      }

      return {
        success: true,
        message: resData.message || 'Hostel rates fetched successfully',
        data: ratesData,
        totalRecords: resData.totalRecords || ratesData.length || 0,
      };
    }

    return {
      success: false,
      message: resData?.message || 'Failed to fetch hostel rates',
      data: [],
      totalRecords: 0,
    };
  } catch (error) {
    console.error('Error fetching hostel rates:', error);

    return {
      success: false,
      message: 'Something went wrong while fetching hostel rates',
      error: error.response?.data?.message || error.message,
      data: [],
      totalRecords: 0,
    };
  }
};
/**
 * Submit application after stage change
 * @param {object} payload - { formId, leadId, fieldJson }
 * @returns {Promise} Response with submission status
 */
export const submitApplicationAfterStageChange = async (payload) => {
  try {
    const response = await axiosInstance.post('/api/Applicant/SubmitApplicationAfterStageChange', payload);
    const resData = response.data;

    // Success case
    if (resData?.statusCode === 200 || resData?.success) {
      return {
        success: true,
        message: resData.message || 'Application submitted successfully',
        data: resData.data,
      };
    }

    // Failure response from API
    return {
      success: false,
      message: resData?.message || 'Failed to submit application',
      data: null,
    };
  } catch (error) {
    console.error('Error submitting application after stage change:', error);

    return {
      success: false,
      message: 'Something went wrong while submitting application',
      error: error.response?.data?.message || error.message,
      data: null,
    };
  }
};

/**
 * Delete application by Lead ID or User ID
 * @param {string} leadId - Lead ID or User ID representing the candidate
 * @returns {Promise} API response
 */
export const deleteApplication = async (leadId) => {
  try {
    if (!leadId) {
      return {
        success: false,
        message: 'Lead ID is required',
        data: null,
      };
    }

    const response = await axiosInstance.post(`/api/Applicant/DeleteApplication/${leadId}`);
    const resData = response.data;

    if (resData?.statusCode === 200 && resData?.status === 'Success') {
      return {
        success: true,
        message: resData.message || 'Application deleted successfully',
        data: resData.data,
      };
    }

    return {
      success: false,
      message: resData?.message || 'Failed to delete application',
      data: null,
    };
  } catch (error) {
    console.error('Error deleting application:', error);

    return {
      success: false,
      message: error.message || error.response?.data?.message || 'Something went wrong while deleting application',
      error: error.message || error.response?.data?.message || error.message,
      data: null,
    };
  }
};

/**
 * Global search across leads, applicants, and students by name, email, or phone.
 * @param {string} query - Search term (min 2 chars)
 * @param {number} limit  - Max results per category (default 8)
 * @returns {Promise<{ leads: [], applicants: [], students: [] }>}
 */
export const globalSearch = async (query, limit = 8) => {
  try {
    if (!query || query.trim().length < 2) {
      return { success: false, data: null };
    }

    const response = await axiosInstance.get('/api/Leads/GlobalSearch', {
      params: { query: query.trim(), limit },
    });

    const resData = response.data;

    if (resData?.statusCode === 200 && (resData?.status === 'Success' || resData?.status === 'success')) {
      let parsed = resData.data;
      if (typeof parsed === 'string') {
        try { parsed = JSON.parse(parsed); } catch { parsed = null; }
      }
      return {
        success: true,
        data: {
          leads:      parsed?.leads      ?? [],
          applicants: parsed?.applicants ?? [],
          students:   parsed?.students   ?? [],
        },
      };
    }

    return { success: false, data: null };
  } catch (error) {
    console.error('Error in globalSearch:', error);
    return { success: false, data: null };
  }
};

